import type * as React from 'react';
import {
  createElement,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

interface BaseDialogProps {
  onAnimationEnd?: () => void;
  onOpenChange: (open: boolean) => void;
  open?: boolean;
}

export function useDialog<
  TProps extends BaseDialogProps,
  TOpenProps extends Partial<Omit<TProps, 'onOpenChange' | 'open'>>,
  TOpenArgs = undefined,
  TOpenFn = (...args: undefined extends TOpenArgs ? [args?: TOpenArgs] : [args: TOpenArgs]) => void
>(options: {
  component: React.ComponentType<TProps>;
  openFunctionArgsToProps?: (args: TOpenArgs) => TOpenProps;
  props?: Omit<
    TProps,
    (TOpenArgs extends undefined ? never : keyof TOpenProps) | 'onOpenChange' | 'open'
  >;
}): {
  close: () => void;
  element: React.ReactNode;
  isOpen: boolean;
  open: TOpenFn;
} {
  const { component, openFunctionArgsToProps, props } = options;

  const id = useId();
  const [idCounter, setIdCounter] = useState<number>(0);

  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const isOpenRef = useRef(isOpen);
  useLayoutEffect(() => {
    isOpenRef.current = isOpen;
  });

  const openFunctionArgsToPropsRef = useRef(openFunctionArgsToProps);
  useLayoutEffect(() => {
    openFunctionArgsToPropsRef.current = openFunctionArgsToProps;
  });

  const propsRef = useRef(props);
  useLayoutEffect(() => {
    propsRef.current = props;
  }, [props]);

  const [openProps, setOpenProps] = useState<TOpenProps>();

  const open = useCallback((args: TOpenArgs) => {
    setIdCounter(prev => prev + 1);
    setIsAnimating(true);
    setIsOpen(true);

    if (openFunctionArgsToPropsRef.current) {
      setOpenProps(openFunctionArgsToPropsRef.current(args));
    }
  }, []) as TOpenFn;

  const close = useCallback(() => {
    setIsAnimating(true);
    setIsOpen(false);
  }, []);

  const element = useMemo(() => {
    if (!isOpen && !isAnimating) {
      return;
    }

    const mergedProps = {
      ...propsRef.current,
      ...openProps,
      onAnimationEnd: () => {
        setIsAnimating(false);

        if (!isOpenRef.current) {
          setOpenProps(undefined);
        }

        if (openProps && 'onAnimationEnd' in openProps) {
          openProps.onAnimationEnd?.();
        } else if (propsRef.current && 'onAnimationEnd' in propsRef.current) {
          (propsRef.current as TProps).onAnimationEnd?.();
        }
      },
      onOpenChange: value => {
        if (!value) {
          setIsAnimating(true);
        }
        setIsOpen(value);
      },
      open: isOpen
    } as TProps;

    return createElement(component, {
      ...mergedProps,
      key: `${id}-${idCounter}`
    });
  }, [isAnimating, component, id, idCounter, isOpen, openProps]);

  return {
    close,
    element,
    isOpen,
    open
  };
}
